<template>
  <div class="w-full px-6 py-4">
    <o-table
      v-if="_service"
      class="w-full"
      container-class="border rounded bg-white dark:bg-gray-900 dark:border-gray-800 overflow-hidden"
      :data="_service.forms"
      :headers="[
        {text: $t('general.form')},
        {text: $t('general.frequency')},
        {text: $t('components.service.edit.forms.requirement')},
        {text: $t('general.actions')}
      ]"
      :limit="20"

      :title="$t('components.service.edit.forms.title')"
      :info="$t('components.service.edit.forms.info')"
      striped
    >
      <template #controls>
        <o-button variant="primary" @click="handleAddServiceForm">
          {{ $t('form.add') }}
        </o-button>
      </template>

      <template #row="{ row, tdClass }">
        <td :class="tdClass">
          {{ row.form?.title }}
        </td>
        <td :class="tdClass">
          {{ getFrequencyLabel(row) }}
        </td>
        <td :class="tdClass">
          {{ getRequirementModeLabel(row) }} <span v-if="row.isRequirementModeScheduled">{{ $t('components.service.edit.forms.hours', { value: row.scheduleOffsetValue, mode: row.scheduleOffsetMode }) }}</span>
        </td>
        <td :class="tdClass">
          <div class="flex items-center space-x-1">
            <o-tooltip :text="$t('general.edit')">
              <o-button icon="edit" size="sm" @click="handleEditServiceForm(row)" />
            </o-tooltip>
            <o-tooltip :text="$t('general.delete')">
              <o-button icon="delete" variant="danger" size="sm" flat @click="handleRemoveServiceForm(row)" />
            </o-tooltip>
          </div>
        </td>
      </template>
    </o-table>

    <AddServiceFormModal
      ref="addServiceFormModal"
      :service="_service"
      @success="handleSuccess"
    />

    <EditServiceFormModal
      ref="editServiceFormModal"
      :service="_service"
      @success="handleSuccess"
    />

    <RemoveServiceFormModal
      ref="removeServiceFormModal"
      :service="_service"
      @success="handleSuccess"
    />
  </div>
</template>

<script setup lang="ts">
import AddServiceFormModal from '@/components/service/actions/AddForm.vue'
import EditServiceFormModal from '@/components/service/actions/EditForm.vue'
import RemoveServiceFormModal from '@/components/service/actions/RemoveForm.vue'
import { OTable } from '@/components/elements'

import {
  ServiceFormFrequencyLabels,
  ServiceFormRequirementModeLabels,
} from '~/_api/models/serviceForm'
import type { Service } from '~/_api/models/service'
import type { ServiceForm } from '~/_api/models/serviceForm'

const props = defineProps<{
  service: Service
}>()

const emit = defineEmits<{
  (e: 'update:service', service: Service): void
  (e: 'update'): void
}>()

const { $i18n } = useNuxtApp()
const $t = $i18n.t.bind($i18n)

// Template refs
const addServiceFormModal = ref()
const editServiceFormModal = ref()
const removeServiceFormModal = ref()

const _service = computed({
  get: () => props.service,
  set: (value) => emit('update:service', value),
})

const getFrequencyLabel = (form: ServiceForm): string => {
  return $t(ServiceFormFrequencyLabels[form.frequency] || 'general.unknown')
}

const getRequirementModeLabel = (form: ServiceForm): string => {
  return $t(ServiceFormRequirementModeLabels[form.requirementMode] || 'general.unknown')
}

const handleAddServiceForm = (): void => {
  addServiceFormModal.value?.open()
}

const handleEditServiceForm = (serviceForm: ServiceForm): void => {
  editServiceFormModal.value?.open(serviceForm)
}

const handleRemoveServiceForm = (serviceForm: ServiceForm): void => {
  removeServiceFormModal.value?.open(serviceForm)
}

const handleSuccess = (): void => {
  emit('update')
}
</script>
